import { VALIDATION_CONSTANTS } from 'global/constants';

import { DUPLICATE_PARTICIPANT_EMAIL } from './YEventRegistration';
import { YParticipant } from './YParticipant';

const getParticipantEmail = (participant) => (participant?.eMail || '').trim();

export const hasOriginalParticipantEmail = (participant) =>
  Boolean(participant?.netzPortalEmail && participant?.eMail === participant?.netzPortalEmail);

// Derived from the value instead of the formik error, because formik errors are keyed by
// participant index and lag one render behind whenever participants are removed or reordered.
const getParticipantEmailError = (email) => {
  const normalizedEmail = (email || '').trim();

  if (!normalizedEmail) {
    return VALIDATION_CONSTANTS.MANDATORY;
  }

  return YParticipant.fields.eMail.isValidSync(normalizedEmail)
    ? undefined
    : VALIDATION_CONSTANTS.INVALID_EMAIL;
};

export const getVisibleParticipantEmailError = (
  error,
  index,
  activeParticipantEmailIndex,
  participantEmail,
  participants
) => {
  const normalizedEmail = (participantEmail || '').trim().toLowerCase();
  const isDuplicateOf = (predicate) =>
    Boolean(normalizedEmail) &&
    participants.some(
      (participant, participantIndex) =>
        predicate(participantIndex) &&
        (participant?.eMail || '').trim().toLowerCase() === normalizedEmail
    );

  // Flagged on the row being edited and on every later duplicate, so that the row keeping the
  // submit button disabled always shows why.
  if (
    isDuplicateOf((participantIndex) => participantIndex < index) ||
    (activeParticipantEmailIndex === index &&
      isDuplicateOf((participantIndex) => participantIndex !== index))
  ) {
    return DUPLICATE_PARTICIPANT_EMAIL;
  }

  const emailError = getParticipantEmailError(participantEmail);
  if (emailError) {
    return emailError;
  }

  if (!error || error === DUPLICATE_PARTICIPANT_EMAIL) {
    return undefined;
  }

  return error;
};

const hasParticipantEmailDuplicates = (participants = []) => {
  const seenEmails = new Set();

  return participants.some((participant) => {
    const email = getParticipantEmail(participant).toLowerCase();
    if (!email) {
      return false;
    }

    if (seenEmails.has(email)) {
      return true;
    }

    seenEmails.add(email);
    return false;
  });
};

const participantNeedsEmailAttention = (
  participant,
  editingParticipantEmailIds = new Set()
) => {
  if (hasOriginalParticipantEmail(participant)) {
    return false;
  }

  if (editingParticipantEmailIds.has(participant?.id)) {
    return true;
  }

  return Boolean(getParticipantEmailError(getParticipantEmail(participant)));
};

export const hasPendingParticipantEmails = (participants = [], editingParticipantEmailIds) =>
  hasParticipantEmailDuplicates(participants) ||
  participants.some((participant) =>
    participantNeedsEmailAttention(participant, editingParticipantEmailIds)
  );

export const syncEditingParticipantEmailIds = (participants = [], previousIds = new Set()) => {
  const nextIds = new Set();

  participants.forEach((participant) => {
    if (hasOriginalParticipantEmail(participant)) {
      return;
    }

    if (previousIds.has(participant?.id) || !getParticipantEmail(participant)) {
      nextIds.add(participant?.id);
    }
  });

  const hasChanges =
    nextIds.size !== previousIds.size ||
    [...nextIds].some((participantId) => !previousIds.has(participantId));

  return hasChanges ? nextIds : previousIds;
};
